export const LAST_OPENED_GRID_KEY = '<grid-id>';

const extractActiveGridIdFromObject = (value: unknown): string | null => {
  if (!value || typeof value !== 'object') return null;
  const obj = value as { state?: { activeGridId?: unknown }; activeGridId?: unknown };
  const activeGridId = obj.state?.activeGridId ?? obj.activeGridId;
  return activeGridId ? String(activeGridId).trim() : null;
};

export const getStoredActiveGridId = (): string | null => {
  const rawValue = localStorage.getItem(LAST_OPENED_GRID_KEY);
  if (!rawValue) return null;

  try {
    let parsed: unknown = rawValue;
    for (let i = 0; i < 3; i++) {
      if (typeof parsed !== 'string') break;
      parsed = JSON.parse(parsed);
    }
    const fromParsed = extractActiveGridIdFromObject(parsed);
    if (fromParsed) return fromParsed;
  } catch {
    // continue with regex fallback
  }

  const directMatch = rawValue.match(/"activeGridId"\s*:\s*"([^"]+)"/);
  if (directMatch?.[1]) return directMatch[1].trim();

  const escapedMatch = rawValue.match(/\\"activeGridId\\"\s*:\s*\\"([^\\"]+)\\"/);
  if (escapedMatch?.[1]) return escapedMatch[1].trim();

  return null;
};

export const listIndexedDbNames = async (): Promise<string[]> => {
  const indexedDbWithDatabases = window.indexedDB as IDBFactory & {
    databases?: () => Promise<Array<{ name?: string | null }>>;
  };

  if (!indexedDbWithDatabases?.databases) {
    return [];
  }

  const dbs = await indexedDbWithDatabases.databases();
  return (dbs ?? []).map((db) => db.name).filter((name): name is string => Boolean(name));
};

export const deleteIndexedDbByName = (databaseName: string): Promise<void> =>
  new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('IndexedDB is not available in this browser.'));
      return;
    }

    const request = window.indexedDB.deleteDatabase(databaseName);
    request.onsuccess = () => resolve();
    request.onblocked = () => reject(new Error(`Database "${databaseName}" is blocked by another open tab.`));
    request.onerror = () => reject(request.error || new Error(`Failed to delete "${databaseName}"`));
  });

export const deleteIndexedDbsByName = async (
  databaseNames: string[]
): Promise<{ succeeded: string[]; failed: string[] }> => {
  const results = await Promise.allSettled(databaseNames.map((name) => deleteIndexedDbByName(name)));
  const succeeded = databaseNames.filter((_, index) => results[index]?.status === 'fulfilled');
  const failed = databaseNames.filter((_, index) => results[index]?.status === 'rejected');
  return { succeeded, failed };
};
